/**
 * Contract View Component
 * Szerződés részletes megtekintése
 */

import React from 'react';
import Modal from '../common/Modal';
import { CustomerContract, CONTRACT_STATUS_OPTIONS, CONTRACT_TYPE_OPTIONS, BILLING_FREQUENCY_OPTIONS } from '../../types/customerContract';
import { formatDate } from '../../utils/dateHelpers';
import {
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  TagIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface ContractViewProps {
  isOpen: boolean;
  onClose: () => void;
  contract: CustomerContract;
}

const ContractView: React.FC<ContractViewProps> = ({ isOpen, onClose, contract }) => {
  const getStatusColor = (status: string) => {
    const option = CONTRACT_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || 'gray';
  };

  const getStatusBadgeClass = (status: string) => {
    const color = getStatusColor(status);
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-700',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-600'
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Szerződés részletei" size="7xl">
      <div className="space-y-6">
        {/* Fejléc */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold text-blue-900 dark:text-blue-300">
                {contract.contractName}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                {contract.contractNumber}
              </p>
              {contract.generatedFrom && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  📄 Generálva: {contract.generatedFrom.sourceName}
                </p>
              )}
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClass(contract.status)}`}>
              {CONTRACT_STATUS_OPTIONS.find(opt => opt.value === contract.status)?.label}
            </span>
          </div>
        </div>

        {/* Alapadatok */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-2">
              <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Szerződés típusa</h4>
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              {CONTRACT_TYPE_OPTIONS.find(opt => opt.value === contract.contractType)?.icon}{' '}
              {CONTRACT_TYPE_OPTIONS.find(opt => opt.value === contract.contractType)?.label}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-2">
              <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Időtartam</h4>
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              {formatDate(contract.startDate)}
              {contract.endDate && (
                <span> - {formatDate(contract.endDate)}</span>
              )}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-2">
              <CurrencyDollarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Díjak</h4>
            </div>
            {/* Ismétlődő díjak összege */}
            <div className="mb-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">🔄 Ismétlődő (havi):</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {contract.services
                  ?.filter(s => s.billingType === 'recurring')
                  .reduce((sum, s) => sum + s.totalPrice, 0)
                  .toLocaleString()} {contract.currency}
              </p>
            </div>
            {/* Egyszeri díjak összege */}
            {contract.services?.some(s => s.billingType === 'one_time') && (
              <div className="mb-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">1️⃣ Egyszeri díjak:</p>
                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  {contract.services
                    ?.filter(s => s.billingType === 'one_time')
                    .reduce((sum, s) => sum + s.totalPrice, 0)
                    .toLocaleString()} {contract.currency}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {BILLING_FREQUENCY_OPTIONS.find(opt => opt.value === contract.billingFrequency)?.icon}{' '}
              {BILLING_FREQUENCY_OPTIONS.find(opt => opt.value === contract.billingFrequency)?.label}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-2">
              <CheckCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Automatikus megújítás</h4>
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              {contract.autoRenew ? '✅ Igen' : '❌ Nem'}
              {contract.renewalNoticeDays && (
                <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">
                  Értesítés: {contract.renewalNoticeDays} nappal előtte
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Szolgáltatások */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center mb-4">
            <TagIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <h4 className="font-semibold text-green-900 dark:text-green-300">
              Szolgáltatások ({contract.services.length})
            </h4>
          </div>

          <div className="space-y-3">
            {contract.services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                        {service.productName}
                      </h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({service.productCode})
                      </span>
                      {/* Díjtípus badge */}
                      {service.billingType === 'one_time' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                          1️⃣ Egyszeri díj
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          🔄 Ismétlődő
                        </span>
                      )}
                    </div>

                    {service.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {service.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Mennyiség:</span>
                        <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">
                          {service.quantity} db
                        </span>
                      </div>

                      {service.listPrice !== service.contractPrice && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Listaár:</span>
                          <span className="ml-1 line-through text-gray-400">
                            {service.listPrice.toLocaleString()} Ft
                          </span>
                        </div>
                      )}

                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Szerződéses ár:</span>
                        <span className="ml-1 font-semibold text-green-600 dark:text-green-400">
                          {service.contractPrice.toLocaleString()} Ft
                        </span>
                      </div>

                      {service.discount && service.discount > 0 && (
                        <div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                            💰 -{service.discount}% kedvezmény
                          </span>
                        </div>
                      )}
                    </div>

                    {service.hourPool && (
                      <div className={`mt-3 rounded-md p-3 border ${
                        service.billingType === 'one_time' 
                          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      }`}>
                        <div className="flex items-center mb-2">
                          <ClockIcon className={`h-4 w-4 mr-2 ${
                            service.billingType === 'one_time'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`} />
                          <span className={`text-sm font-semibold ${
                            service.billingType === 'one_time'
                              ? 'text-orange-900 dark:text-orange-300'
                              : 'text-blue-900 dark:text-blue-300'
                          }`}>
                            {service.billingType === 'one_time' ? '⏱️ Benne foglalt órák (egyszeri)' : 'Órakeret (havi)'}
                          </span>
                        </div>
                        <div className={`grid ${service.billingType === 'one_time' ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'} gap-2 text-xs`}>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Foglalt:</span>
                            <span className={`ml-1 font-semibold ${
                              service.billingType === 'one_time'
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-blue-600 dark:text-blue-400'
                            }`}>
                              {service.hourPool.includedHours} óra
                            </span>
                          </div>
                          {service.billingType === 'recurring' && (
                            <>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Felhasznált:</span>
                                <span className="ml-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {service.hourPool.usedHours} óra
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Maradt:</span>
                                <span className="ml-1 font-semibold text-green-600 dark:text-green-400">
                                  {service.hourPool.remainingHours} óra
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Túlóra:</span>
                                <span className="ml-1 font-semibold text-red-600 dark:text-red-400">
                                  {service.hourPool.overageHours} óra
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        {service.billingType === 'recurring' && service.hourPool.overageRate && service.hourPool.overageRate > 0 && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                            Túlóra díja: <span className="font-semibold">{service.hourPool.overageRate.toLocaleString()} Ft/óra</span>
                          </div>
                        )}
                      </div>
                    )}

                    {service.priceNote && (
                      <div className="mt-2 text-xs italic text-gray-600 dark:text-gray-400">
                        💡 {service.priceNote}
                      </div>
                    )}

                    {/* Csomag tartalom megjelenítése */}
                    {service.packageServices && service.packageServices.length > 0 && (
                      <div className="mt-3 bg-purple-50 dark:bg-purple-900/20 rounded-md p-3 border border-purple-200 dark:border-purple-700">
                        <div className="text-xs font-semibold text-purple-900 dark:text-purple-300 mb-2">
                          📦 Csomag tartalma ({service.packageServices.length} tétel)
                        </div>
                        <div className="space-y-1">
                          {service.packageServices.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-purple-600 dark:text-purple-400">•</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.productName}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  ({item.productCode})
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-600 dark:text-gray-400">
                                  {item.quantity} {item.unit || 'db'}
                                  {item.hours && ` (${item.hours} óra)`}
                                </span>
                                {item.isIncluded && (
                                  <span className="text-green-600 dark:text-green-400 text-xs">
                                    ✓ Benne
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-right ml-4">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {service.totalPrice.toLocaleString()} Ft
                    </div>
                    {service.taxRate && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        + {service.taxRate} ÁFA
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Megjegyzések */}
        {contract.notes && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Megjegyzések</h4>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{contract.notes}</p>
          </div>
        )}

        {/* Szerződési feltételek */}
        {contract.terms && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Szerződési feltételek</h4>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{contract.terms}</p>
          </div>
        )}

        {/* Kapcsolódó task-ok és számlák */}
        {(contract.taskIds && contract.taskIds.length > 0) || (contract.invoiceIds && contract.invoiceIds.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contract.taskIds && contract.taskIds.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Kapcsolódó task-ok ({contract.taskIds.length})
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {contract.taskIds.length} task kapcsolódik ehhez a szerződéshez
                </div>
              </div>
            )}

            {contract.invoiceIds && contract.invoiceIds.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Számlák ({contract.invoiceIds.length})
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {contract.invoiceIds.length} számla generálva
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bezárás gomb */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ContractView;
